// Shared summary statistics — the SINGLE source of truth for the per-employee
// overtime / vacation figures shown on both the Summary page
// (components/summary/SummaryEmployeeList.jsx) and the Dashboard
// (pages/Dashboard.jsx). Keeping the maths here prevents the two surfaces from
// drifting (the Dashboard previously summed all weeks and ignored paid
// overtime, so it disagreed with the Summary).

// Current ISO-ish calendar week, matching the Summary page's own helper so both
// surfaces agree on "which week is now". Optional `now` for testability.
export const getCurrentISOWeek = (now = new Date()) => {
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const daysSinceJan4 = Math.floor((now - jan4) / 86400000);
  const weekNumber = Math.ceil((daysSinceJan4 + jan4.getDay() + 1) / 7);
  return Math.max(1, weekNumber);
};

// Default "up to calendar week" cutoff = the last completed week. Mirrors the
// Summary page's default so balances exclude the current (in-progress) and
// future weeks.
export const getDefaultCalendarWeek = (now = new Date()) => {
  const currentWeek = getCurrentISOWeek(now);
  return currentWeek > 1 ? currentWeek - 1 : 1;
};

/**
 * Compute an employee's headline figures exactly like the Summary page.
 *
 * @param {object} employee - a SummaryEmployeeDTO (with summaryCalendarEmployees).
 * @param {object} [opts]
 * @param {number[]} [opts.years] - years to include (default: all present).
 * @param {number} [opts.upToWeek] - inclusive week cutoff (default: no cutoff).
 * @returns {{
 *   grossMinutes:number, paidOvertimeMinutes:number, netMinutes:number,
 *   overtimeHours:number, remainingVacation:number, usedVacationDays:number,
 *   vacationCarryover:number, completedWeeks:number
 * }}
 */
export const computeEmployeeStats = (employee, { years, upToWeek } = {}) => {
  const empty = {
    grossMinutes: 0,
    paidOvertimeMinutes: 0,
    netMinutes: 0,
    overtimeHours: 0,
    remainingVacation: 0,
    usedVacationDays: 0,
    vacationCarryover: 0,
    completedWeeks: 0,
  };
  if (!employee) return empty;

  const paidOvertimeMinutes =
    (employee.totalPaidOvertimeHours || 0) * 60 + (employee.totalPaidOvertimeMinutes || 0);

  // Sort oldest-first so the once-per-year holiday allowance is added on the
  // first week of each year — identical to the Summary page.
  const sorted = (employee.summaryCalendarEmployees || [])
    .slice()
    .sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.weekCalendarNumber - b.weekCalendarNumber
    );

  const afterYearFilter = years ? sorted.filter((e) => years.includes(e.year)) : sorted;
  const filtered = upToWeek
    ? afterYearFilter.filter((e) => e.weekCalendarNumber <= upToWeek)
    : afterYearFilter;

  // remainingVacation walks the same accumulation the Summary uses for its
  // "Remaining Vacation (Current Year)" stat: carryover + (yearly allowance,
  // once per year present) − Σ(actual holiday days used).
  let remainingVacation = employee.remainingHolidays || 0;
  let lastYearCounted = null;
  let usedVacationDays = 0;
  let diffMinutes = 0;

  filtered.forEach((entry) => {
    const actual = entry.actualWorkTimeHours * 60 + entry.actualWorkTimeMinutes;
    const should = entry.shouldWorkTimeHours * 60 + entry.shouldWorkTimeMinutes;
    diffMinutes += actual - should;

    if (lastYearCounted !== entry.year) {
      remainingVacation += entry.shouldHolidayDaysInYear || 0;
      lastYearCounted = entry.year;
    }
    remainingVacation -= entry.actualHolidayDays || 0;
    usedVacationDays += entry.actualHolidayDays || 0;
  });

  const grossMinutes = diffMinutes + (employee.remainingOvertime || 0) * 60;
  const netMinutes = grossMinutes - paidOvertimeMinutes;

  return {
    grossMinutes,
    paidOvertimeMinutes,
    netMinutes,
    overtimeHours: netMinutes / 60,
    remainingVacation,
    usedVacationDays,
    vacationCarryover: employee.remainingHolidays || 0,
    completedWeeks: filtered.length,
  };
};
